/**
 * For SEO we need to add only one video per schema in the structured data.
    The logic should be this:
    1. If there is a hero video I add this video.
    2. If there is no hero video I add next one in the page.

    For the thumbnail the logic is as follows:
    1. If there is a thumbnail I add it to the schema.
    2. If it's a YouTube video I take the original thumbnail
    3. If there is none I take the meta image
    4. I add a default thumbnail.

    For title and description:
    1. If there is a meta title and a meta description of the video I add them.
    2. Else I take the meta of the page
 */

import { MetaProps } from '../../../../types/IHeader';
import { VideoObject } from 'schema-dts';
import { siteUrl } from '..';
import { PostMeta } from '../../utils';

export const convertUrlVideoToEmbedUrl = (url: string) => {
  const id = getIdFromUrl(url);
  if (url.includes('youtu')) {
    const idYt = getYouTubeId(url, id);

    return `https://www.youtube.com/embed/${idYt}`;
  } else if (url.includes('/vimeo')) {
    return `https://player.vimeo.com/video/${id}`;
  }
  return url;
};

export const getIdFromUrl = (url: string) => {
  let urlSplit = url.split('?');
  urlSplit = urlSplit[0].split('/');
  return urlSplit[urlSplit.length - 1];
};

export const getYouTubeId = (url: string, id: string | null) => {
  if (url.includes('youtu')) {
    const convertUrl = new URL(url);
    let idYt = convertUrl?.searchParams.get('v');

    if (!idYt) {
      idYt = id;
    }

    return idYt;
  }

  return null;
};

export const getYoutubeImage = (url: string, id: string) => {
  if (url.includes('youtu')) {
    const convertUrl = new URL(url);
    let idYt = convertUrl?.searchParams.get('v');
    if (!idYt) {
      idYt = id;
    }
    return `https://i3.ytimg.com/vi/${idYt}/maxresdefault.jpg`;
  }
  return null;
};

export const video = (
  data: MetaProps,
  uri: string,
  localeCode: string,
  index?: number,
  postMeta?: PostMeta,
): VideoObject | null => {
  const videoUrl =
    index !== undefined && data.blocks
      ? data?.blocks[index].url
      : data?.hero?.[0].videoLink || data?.hero?.[0]?.vimeoLink;

  if (!videoUrl) return null;

  const embedUrl = convertUrlVideoToEmbedUrl(videoUrl);

  const id = getIdFromUrl(videoUrl);
  const youtubImage = getYoutubeImage(videoUrl, id);

  let metaName: string | undefined;
  let metaDescription: string | undefined;
  let metaImage: string | null | undefined;

  if (index !== undefined && data.blocks) {
    metaName = data?.blocks[index]?.metaTitle;
    metaDescription = data?.blocks[index]?.metaDescription;
    metaImage = data?.blocks[index]?.metaImage?.url || youtubImage;
  }

  return {
    '@type': 'VideoObject',
    '@id': `${siteUrl}${uri}#video${index ? '-' + index : ''}`,
    name: metaName ?? data?.metaFields?.title,
    thumbnailUrl:
      metaImage ||
      data?.hero?.[0]?.image?.url ||
      data?.hero?.[0]?.vimeoImage?.url ||
      data?.hero?.[0]?.videoImage?.url ||
      youtubImage ||
      postMeta?.fallbackVideoImage?.url + '?auto=format&h=675&w=1200',
    description: metaDescription ?? data?.metaFields?.description,
    embedUrl: embedUrl,
    isFamilyFriendly: true,
    inLanguage: localeCode,
    uploadDate: data?.meta?.publishedAt || data?.date,
  };
};
